/**
 * Wixtar 整合 API Service
 */

import axios from '@/api/axios.js';

/**
 * Wixtar 點數異動類型（point_type）
 * 與後端 WIXTAR_POINT_TYPE 對應，呼叫增加點數 API 時可帶入，未傳則後端預設 8（活動贈點）
 */
export const WIXTAR_POINT_TYPE = {
  SALES_ADD: 1,
  SALES_RETURN: 2,
  REDEEM: 3,
  REDEEM_RETURN: 4,
  TRANSFER_OUT: 5,
  TRANSFER_IN: 6,
  ACTIVITY_DEDUCT: 7,
  /** 8: 活動贈點(+) → vip00.total_point，抽獎給點等使用 */
  ACTIVITY_GIFT: 8
};

export const wixtarService = {
  /**
   * 查詢 Wixtar 配置
   * @param {boolean} decrypt - 是否解密敏感資訊（編輯模式使用）
   */
  async getConfig(decrypt = false) {
    console.log('[Wixtar API] 開始查詢配置...', { decrypt });
    try {
      // axios interceptor 已經返回 response.data，所以這裡直接返回 response
      const params = decrypt ? { decrypt: true } : {};
      const response = await axios.get('/integrations/wixtar/config', { params });
      console.log('[Wixtar API] 查詢配置回應:', response);
      console.log('[Wixtar API] 回應類型:', typeof response);
      console.log('[Wixtar API] response.success:', response?.success);
      console.log('[Wixtar API] response.data:', response?.data);
      return response;
    } catch (error) {
      console.error('[Wixtar API] 查詢配置失敗:', error);
      console.error('[Wixtar API] 錯誤詳情:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * 設定 Wixtar 配置
   */
  async setConfig(configData) {
    console.log('[Wixtar API] 發送設定請求:', {
      apiUrl: configData.apiUrl,
      apiVersion: configData.apiVersion,
      wixtarClientId: configData.wixtarClientId,
      wixtarClientSecret: configData.wixtarClientSecret ? '***已填寫***' : '未填寫',
      webhookSecret: configData.webhookSecret ? '***已填寫***' : '未填寫',
      syncMembers: configData.syncMembers,
      syncOrders: configData.syncOrders,
      syncPoints: configData.syncPoints,
      syncTickets: configData.syncTickets,
      isActive: configData.isActive
    });

    try {
      const response = await axios.post('/integrations/wixtar/config', configData);
      console.log('[Wixtar API] 設定請求成功:', response);
      // axios interceptor 已回傳標準格式 { success, data, message }
      return response;
    } catch (error) {
      console.error('[Wixtar API] 設定請求失敗:', error);
      console.error('[Wixtar API] 錯誤詳情:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      throw error;
    }
  },

  /**
   * 查詢同步記錄
   * 篩選選「全部」時會帶空字串，後端 enum 不接受，這裡省略空白參數。
   * axios 攔截器已回傳 { success, data }，不要再取 .data。
   */
  async getSyncLogs(params = {}) {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        query[key] = value;
      }
    });
    return axios.get('/integrations/wixtar/sync/logs', { params: query });
  },

  /**
   * 手動觸發會員同步
   */
  async syncMember(memberId) {
    const response = await axios.post(`/integrations/wixtar/sync/member/${memberId}`);
    return response.data;
  },

  /**
   * 手動觸發訂單同步
   */
  async syncOrder(orderId) {
    const response = await axios.post(`/integrations/wixtar/sync/order/${orderId}`);
    return response.data;
  },

  /**
   * 手動同步會員票券至本地鏡像（供到期提醒掃描）
   *
   * webhook 只能捕捉上線後才變動的票券，存量票券需靠此補齊
   * @param {string} scope - pushable（僅同步可推播的 LINE 好友）| all
   */
  async syncTicketMirror(scope = 'pushable') {
    const response = await axios.post('/integrations/wixtar/tickets/mirror-sync', { scope });
    return response.data;
  },

  /**
   * 查詢票券鏡像同步狀態（最近一次批次與鏡像統計）
   */
  async getTicketMirrorSyncStatus() {
    const response = await axios.get('/integrations/wixtar/tickets/mirror-sync/status');
    return response.data;
  },

  /**
   * 手動同步會員點數餘額與歷史明細至本地鏡像
   * @param {string} scope - pushable | all
   */
  async syncPointMirror(scope = 'pushable') {
    const response = await axios.post('/integrations/wixtar/points/mirror-sync', { scope });
    return response.data;
  },

  /**
   * 查詢點數鏡像同步狀態（批次、可同步人數、ledger 統計）
   */
  async getPointMirrorSyncStatus() {
    const response = await axios.get('/integrations/wixtar/points/mirror-sync/status');
    return response.data;
  },

  /**
   * 取得可用的票券活動列表
   */
  async getTicketsActivitys() {
    try {
      const response = await axios.get('/integrations/wixtar/tickets/activitys');
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得票券活動列表失敗:', error);
      throw error;
    }
  },

  /**
   * 查詢 Wixtar 票券目錄（扁平化票券清單，支援搜尋／篩選／分頁）
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.search - 比對票券名稱、票券 ID、活動名稱
   * @param {string} params.activityId - 只看指定票券活動
   * @param {string} params.status - available（可派發）/ sold_out（已發完）
   * @param {string} params.sortBy - activity / name / available
   * @param {string} params.sortOrder - asc / desc
   * @param {boolean} params.refresh - 忽略後端快取重新向 Wixtar 查詢
   * @returns {Promise<Object>} 標準回應 { success, data: { items, pagination, activities, fetchedAt } }
   */
  async getTicketCatalog(params = {}) {
    try {
      return await axios.get('/integrations/wixtar/tickets/catalog', { params });
    } catch (error) {
      console.error('[Wixtar API] 查詢票券目錄失敗:', error);
      throw error;
    }
  },

  async listAllTicketCatalog() {
    const all = [];
    let page = 1;
    let totalPages = 1;
    do {
      const response = await this.getTicketCatalog({
        page,
        pageSize: 100,
        sortBy: 'activity',
        sortOrder: 'asc'
      });
      all.push(...(response?.data?.items || []));
      totalPages = response?.data?.pagination?.totalPages || 1;
      page += 1;
    } while (page <= totalPages && page <= 10);
    return all;
  },

  /**
   * 查詢單一 Wixtar 票券詳情
   * @param {string} ticketId - 票券 ID
   * @param {Object} params - 查詢參數
   * @param {boolean} params.refresh - 忽略後端快取重新向 Wixtar 查詢
   * @returns {Promise<Object>} 標準回應 { success, data, message }
   */
  async getTicketCatalogItem(ticketId, params = {}) {
    try {
      return await axios.get(`/integrations/wixtar/tickets/catalog/${ticketId}`, { params });
    } catch (error) {
      console.error('[Wixtar API] 查詢票券詳情失敗:', error);
      throw error;
    }
  },

  /**
   * 派發 Wixtar 票券給指定會員／手機號碼（逐筆回報成功與失敗）
   * @param {Object} payload
   * @param {string} payload.ticketId - 票券 ID
   * @param {Array<string>} [payload.memberIds] - CRM 會員 ID 清單
   * @param {Array<string>} [payload.phones] - 手機號碼清單
   * @param {string} [payload.reason] - 派券原因，寫入操作紀錄
   * @param {boolean} [payload.sendLineNotification] - 是否推播 LINE 個人訊息
   * @param {string} [payload.lineMsgTemplateId] - 個人訊息範本 ID
   * @returns {Promise<Object>} 標準回應 { success, data: { batchId, successCount, failCount, results } }
   */
  async dispatchTicket(payload) {
    try {
      return await axios.post('/integrations/wixtar/tickets/dispatch', payload);
    } catch (error) {
      console.error('[Wixtar API] 派發票券失敗:', error);
      throw error;
    }
  },

  /**
   * 派券前預檢：回報哪些對象收不到 LINE 推播
   * @param {Object} payload
   * @param {Array<string>} [payload.memberIds] - CRM 會員 ID 清單
   * @param {Array<string>} [payload.phones] - 手機號碼清單
   * @returns {Promise<Object>} 標準回應 { success, data: { pushableCount, notPushableCount, items } }
   */
  async precheckDispatch(payload) {
    try {
      return await axios.post('/integrations/wixtar/tickets/dispatch/precheck', payload);
    } catch (error) {
      console.error('[Wixtar API] 派券預檢失敗:', error);
      throw error;
    }
  },

  /**
   * 依條件篩選派券對象（標籤、等級、RFM、消費行為等）
   *
   * 回傳完整名單而非分頁，前端需把 members 全部帶進派券請求，
   * 只取一頁會導致實際派發對象與畫面顯示不符。
   *
   * @param {Object} filterConfig - 條件格式同批次發券的 filterConfig
   * @returns {Promise<Object>} { success, data: { members, stats, withoutPhoneMembers } }
   */
  async filterDispatchTargets(filterConfig) {
    try {
      return await axios.post('/integrations/wixtar/tickets/dispatch/filter-targets', { filterConfig });
    } catch (error) {
      console.error('[Wixtar API] 派券對象篩選失敗:', error);
      throw error;
    }
  },

  /**
   * 查詢派券操作紀錄列表
   * @param {Object} params - { page, pageSize, status, ticketId, search, sortBy, order }
   * @returns {Promise<Object>} 標準回應 { success, data: { items, pagination } }
   */
  async getDispatchRecords(params = {}) {
    try {
      return await axios.get('/integrations/wixtar/tickets/dispatches', { params });
    } catch (error) {
      console.error('[Wixtar API] 查詢派券紀錄失敗:', error);
      throw error;
    }
  },

  /**
   * 查詢單筆派券紀錄
   * @param {string} batchId - 批次編號
   */
  async getDispatchRecord(batchId) {
    try {
      return await axios.get(`/integrations/wixtar/tickets/dispatches/${batchId}`);
    } catch (error) {
      console.error('[Wixtar API] 查詢派券紀錄詳情失敗:', error);
      throw error;
    }
  },

  /**
   * 查詢單筆派券的逐筆發送明細
   * @param {string} batchId - 批次編號
   * @param {Object} params - { page, pageSize, status, search }
   */
  async getDispatchRecordDetails(batchId, params = {}) {
    try {
      return await axios.get(`/integrations/wixtar/tickets/dispatches/${batchId}/details`, { params });
    } catch (error) {
      console.error('[Wixtar API] 查詢派券明細失敗:', error);
      throw error;
    }
  },

  /**
   * 派發票券
   * @param {string} ticket_no - 票券活動序列號
   * @param {string} vip_id - 會員編號
   */
  async sendTicket(ticket_no, vip_id) {
    try {
      const response = await axios.post('/integrations/wixtar/tickets/send', {
        ticket_no,
        vip_id
      });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 派發票券失敗:', error);
      console.error('[Wixtar API] 錯誤詳情:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * 增加會員累積點數
   * @param {Object} pointsData - 點數資料
   * @param {number} pointsData.accumulated_points - 累積點數
   * @param {number} pointsData.redeem_points - 兌換點數（選填，預設為 0）
   * @param {string} pointsData.store_id - 門市代碼
   * @param {string} pointsData.memberIdentifier - 會員識別碼（電話號碼、Wixtar 會員 ID、CRM 會員 ID 或 LINE User ID）
   * @param {string} pointsData.memberIdentifierType - 識別類型（'phone', 'wixtar_member_id', 'crm_member_id', 'line_user_id'，預設 'phone'）
   * @param {number} [pointsData.point_type] - 點數異動類型（選填，見 WIXTAR_POINT_TYPE，未傳則後端預設 8 活動贈點）
   * @note id 欄位會由後端自動生成不重複的隨機號碼（UUID），不需要提供
   */
  async addMemberAccumulatedPoints(pointsData) {
    try {
      const response = await axios.patch('/integrations/wixtar/members/points', pointsData);
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 增加會員累積點數失敗:', error);
      console.error('[Wixtar API] 錯誤詳情:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * 取得會員資料
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @param {string} params.identifierType - 識別類型（crm_member_id, wixtar_member_id, phone, line_user_id）
   * @param {string} params.source - 來源（SOOCKER）
   */
  async getMemberData(memberId, params = {}) {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/data`, { params });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得會員資料失敗:', error);
      throw error;
    }
  },

  /**
   * 修改會員資料
   * @param {string} memberId - 會員 ID
   * @param {Object} memberData - 會員資料
   * @param {string} identifierType - 識別類型（crm_member_id, wixtar_member_id, phone, line_user_id）
   */
  async updateMemberData(memberId, memberData, identifierType = 'crm_member_id') {
    try {
      const response = await axios.patch(`/integrations/wixtar/members/${memberId}/data`, memberData, {
        params: { identifierType }
      });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 修改會員資料失敗:', error);
      throw error;
    }
  },

  /**
   * 取得消費紀錄
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @param {string} params.identifierType - 識別類型
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   */
  async getMemberOrders(memberId, params = {}) {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/orders`, { params });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得消費紀錄失敗:', error);
      throw error;
    }
  },

  /**
   * 取得消費紀錄明細
   * @param {string} memberId - 會員 ID
   * @param {string} orderId - 訂單 ID
   * @param {string} identifierType - 識別類型
   */
  async getMemberOrderDetails(memberId, orderId, identifierType = 'crm_member_id') {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/orders/${orderId}`, {
        params: { identifierType }
      });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得消費紀錄明細失敗:', error);
      throw error;
    }
  },

  /**
   * 取得會員的票券
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @param {string} params.identifierType - 識別類型
   * @param {string} params.version - API 版本（v2, v3，預設 v2）
   */
  async getMemberTickets(memberId, params = {}) {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/tickets`, { params });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得會員的票券失敗:', error);
      throw error;
    }
  },

  /**
   * 查詢會員 Wixtar 票券鏡像（含 unused／used／expired）
   * @param {string} memberId
   * @param {{ status?: string }} [params]
   */
  async getMemberTicketMirror(memberId, params = {}) {
    const response = await axios.get(`/integrations/wixtar/members/${memberId}/ticket-mirror`, { params });
    return response;
  },

  /**
   * 立即同步單一會員票券鏡像
   * @param {string} memberId
   */
  async syncMemberTicketMirror(memberId) {
    const response = await axios.post(`/integrations/wixtar/members/${memberId}/ticket-mirror/sync`);
    return response;
  },

  /**
   * 取得會員票券明細
   * @param {string} memberId - 會員 ID
   * @param {string} ticketId - 票券 ID
   * @param {string} identifierType - 識別類型
   * @param {string} version - API 版本（v2, v3）
   */
  async getMemberTicketInfo(memberId, ticketId, identifierType = 'crm_member_id', version = 'v2') {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/tickets/${ticketId}`, {
        params: { identifierType, version }
      });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得會員票券明細失敗:', error);
      throw error;
    }
  },

  /**
   * 取得會員紅利點數紀錄
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @param {string} params.identifierType - 識別類型
   * @param {string} params.start_date - 開始日期
   * @param {string} params.end_date - 結束日期
   * @param {string} params.store_id - 門市代碼
   * @param {string} params.time_sort - 時間排序（DEFAULT, ASCEND, DESCEND）
   */
  async getMemberPointsUsageRecords(memberId, params = {}) {
    try {
      const response = await axios.get(`/integrations/wixtar/members/${memberId}/points/usage-records`, { params });
      return response.data;
    } catch (error) {
      console.error('[Wixtar API] 取得會員紅利點數紀錄失敗:', error);
      throw error;
    }
  },

  /**
   * 同步 Wixtar 門市至 CRM
   * 不帶查詢條件時為全量同步，Wixtar 已移除的門市會自動停用；CRM 自建門市不受影響
   * @param {Object} query - Wixtar Get Stores 查詢參數（area、city 等）
   * @returns {Promise<Object>} 同步統計（created、updated、claimed、deactivated）
   */
  async syncStores(query = {}) {
    try {
      const response = await axios.post('/integrations/wixtar/stores/sync', query);
      return response;
    } catch (error) {
      console.error('[Wixtar API] 同步門市失敗:', error);
      throw error;
    }
  }
};
